
import { supabase } from '@/lib/supabase';
import { supabaseCustomRole } from '@/lib/supabase/customRoleClient';
import { toast } from 'sonner';
import { isValidUserId } from '@/utils/authValidation';

/**
 * Check if the favorites table exists and is accessible
 */
const checkFavoritesTableExists = async () => {
  try {
    const { error: customRoleError } = await supabaseCustomRole
      .from('favorites')
      .select('count')
      .limit(0);

    if (!customRoleError) {
      return true;
    }

    const { error: mainClientError } = await supabase
      .from('favorites')
      .select('count')
      .limit(0);

    return !mainClientError;
  } catch (error) {
    console.error("Error checking favorites table:", error);
    return false;
  }
};

export const fetchFavorites = async (userId: string) => {
  if (!isValidUserId(userId)) {
    console.error("Cannot fetch favorites: Invalid userId format", { 
      userId, 
      fromFunc: "fetchFavorites" 
    });
    return [];
  }
  
  try {
    const tableExists = await checkFavoritesTableExists();
    if (!tableExists) {
      toast.error("Errore di configurazione", {
        description: "La tabella dei preferiti non è configurata correttamente."
      });
      return [];
    }

    let favoritesData;
    let favoritesError;
    
    try {
      const result = await supabase
        .from('favorites')
        .select('dealer_id')
        .eq('user_id', userId);
      
      favoritesData = result.data;
      favoritesError = result.error;
    } catch (mainClientError) {
      console.warn('Error using main client for favorites:', mainClientError);
      favoritesError = mainClientError;
    }
    
    if (favoritesError || !favoritesData) {
      const result = await supabaseCustomRole
        .from('favorites')
        .select('dealer_id')
        .eq('user_id', userId);
      
      favoritesData = result.data;
      favoritesError = result.error;
    }
    
    const favoriteIds = (favoritesData || []).map(fav => fav.dealer_id);
    return favoriteIds;
  } catch (error) {
    console.error('Error in fetchFavorites:', error);
    return [];
  }
};

export const addToFavorites = async (userId: string, dealerId: string) => {
  if (!isValidUserId(userId) || !isValidUserId(dealerId)) {
    toast.error("Errore di autenticazione", {
      description: "ID non valido. Prova a effettuare nuovamente l'accesso."
    });
    throw new Error("Invalid ID format");
  }
  
  try {
    const existingFavorites = await fetchFavorites(userId);
    if (existingFavorites.includes(dealerId)) {
      toast.success("Già nei preferiti");
      return;
    }
    
    const tableExists = await checkFavoritesTableExists();
    if (!tableExists) {
      toast.error("Errore di configurazione", {
        description: "La tabella dei preferiti non è configurata correttamente."
      });
      throw new Error("Favorites table not configured");
    }

    let error;
    
    try {
      const result = await supabase
        .from('favorites')
        .insert([{ user_id: userId, dealer_id: dealerId }]);
        
      error = result.error;
      
      if (!error) {
        toast.success("Aggiunto ai preferiti");
        return;
      }
    } catch (mainClientError) {
      error = mainClientError;
    }
    
    if (error && error.code !== '23505') {
      const result = await supabaseCustomRole
        .from('favorites')
        .insert([{ user_id: userId, dealer_id: dealerId }]);
        
      error = result.error;
      
      if (error && error.code !== '23505') {
        throw error;
      }
    }
  } catch (error) {
    console.error("Error in addToFavorites:", error);
    throw error;
  }
};

export const removeFromFavorites = async (userId: string, dealerId: string) => {
  if (!isValidUserId(userId) || !isValidUserId(dealerId)) {
    toast.error("Errore di autenticazione", {
      description: "ID non valido. Prova a effettuare nuovamente l'accesso."
    });
    throw new Error("Invalid ID format");
  }
  
  try {
    const tableExists = await checkFavoritesTableExists();
    if (!tableExists) {
      toast.error("Errore di configurazione", {
        description: "La tabella dei preferiti non è configurata correttamente."
      });
      throw new Error("Favorites table not configured");
    }

    let error;
    
    try {
      const result = await supabaseCustomRole
        .from('favorites')
        .delete()
        .match({ user_id: userId, dealer_id: dealerId });
        
      error = result.error;
      
      if (!error) {
        toast.success("Rimosso dai preferiti");
        return;
      }
    } catch (customRoleError) {
      error = customRoleError;
    }
    
    if (error) {
      const result = await supabase
        .from('favorites')
        .delete()
        .match({ user_id: userId, dealer_id: dealerId });
        
      error = result.error;
      
      if (error) throw error;
      
      toast.success("Rimosso dai preferiti");
    }
  } catch (error) {
    console.error("Error in removeFromFavorites:", error);
    throw error;
  }
};
